import axios from 'axios';
import type {
  Patient,
  Case,
  ModelMatchResponse,
  ModelMatchTaskResponse,
  ModelListItem,
  DoseResult,
  RemedialDoseResult,
  RemedialCandidate,
  PKFormulaResult,
  GridSearchRegimen,
  SafetyCheckResult,
  AdministrationMode,
  AIExtractResponse,
  AIAssistantTurnResponse,
  AIAssistantStreamStatusPayload,
  IntakeContextResponse,
  DrugCovariateScopeMatrixResponse,
  ScopeResolutionResult,
  CalculationRecord,
  AuthActionResponse,
  TokenResponse,
  User,
  AgentRequestedAction,
  AgentFieldSource,
  AdminModelListResponse,
  ModelActivationRequest,
  ModelActivationPreflightResponse,
  ModelActivationResponse,
  ModelPublicationRequest,
  ModelPublicationResponse,
  ModelReleaseListResponse,
  ModelCandidatePackage,
  ModelCandidatePackageListResponse,
  ModelCandidatePackageUploadResponse,
  ModelQualificationRun,
  ModelQualificationRunListResponse,
  ModelWorkflowProjection,
  ModelValidationEvidenceResponse,
  DrugCatalogItem,
  DrugCapabilityResponse,
} from '../types';
import { sanitizeMojibakeText } from '../utils/textSanitizer';

const API_BASE = process.env.REACT_APP_API_URL || '';

function localizeTransportError(error: any): void {
  if (error?.message === 'Network Error') {
    error.message = '网络连接错误，请检查网络或服务状态';
  } else if (error?.code === 'ECONNABORTED' || /timeout/i.test(String(error?.message || ''))) {
    error.message = '请求超时，请稍后重试';
  }
}

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

const AI_REQUEST_TIMEOUT = 120000;
const PK_CALCULATION_TIMEOUT = 240000;
const MODEL_QUALIFICATION_TIMEOUT = 300000;
const SYNTHETIC_DATASET_TIMEOUT = 900000;

function normalizeMultipartHeaders(config: any) {
  if (typeof FormData === 'undefined' || !(config?.data instanceof FormData)) {
    return config;
  }

  const headers = config.headers as any;
  if (headers) {
    if (typeof headers.delete === 'function') {
      headers.delete('Content-Type');
      headers.delete('content-type');
    } else {
      delete headers['Content-Type'];
      delete headers['content-type'];
    }
  }

  return config;
}

// ========== Token 管理 ==========
const TOKEN_KEY = 'precidose_access_token';
const REFRESH_KEY = 'precidose_refresh_token';

export function getStoredToken() { return localStorage.getItem(TOKEN_KEY); }
export function getStoredRefresh() { return localStorage.getItem(REFRESH_KEY); }
export function storeTokens(access: string, refresh: string) {
  localStorage.setItem(TOKEN_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}
export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

const ADMIN_TOKEN_KEY = 'precidose_admin_token';

export function getAdminToken() { return localStorage.getItem(ADMIN_TOKEN_KEY); }
export function storeAdminToken(token: string) { localStorage.setItem(ADMIN_TOKEN_KEY, token); }
export function clearAdminToken() { localStorage.removeItem(ADMIN_TOKEN_KEY); }

export const adminAxios = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

adminAxios.interceptors.request.use(config => {
  normalizeMultipartHeaders(config);
  const token = getAdminToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

adminAxios.interceptors.response.use(
  res => {
    res.data = sanitizeMojibakeText(res.data);
    return res;
  },
  error => {
    localizeTransportError(error);
    if (error.response?.data !== undefined) {
      error.response.data = sanitizeMojibakeText(error.response.data);
    }
    if (error.response?.status === 401) {
      clearAdminToken();
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  },
);

api.interceptors.request.use(config => {
  normalizeMultipartHeaders(config);
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantId = localStorage.getItem('precidose_tenant_id');
  if (tenantId) {
    config.headers['X-Tenant-ID'] = tenantId;
  }
  return config;
});

let refreshPromise: Promise<string> | null = null;

api.interceptors.response.use(
  res => {
    res.data = sanitizeMojibakeText(res.data);
    return res;
  },
  async error => {
    localizeTransportError(error);
    if (error.response?.data !== undefined) {
      error.response.data = sanitizeMojibakeText(error.response.data);
    }
    const original = error.config;
    if (error.response?.status === 401 && !original._retry && !original.url?.includes('/api/auth/')) {
      original._retry = true;
      const refresh = getStoredRefresh();
      if (!refresh) return Promise.reject(error);
      try {
        if (!refreshPromise) {
          refreshPromise = axios.post<TokenResponse>(`${API_BASE}/api/auth/refresh`, { refresh_token: refresh })
            .then(r => {
              storeTokens(r.data.access_token, r.data.refresh_token);
              return r.data.access_token;
            })
            .finally(() => { refreshPromise = null; });
        }
        const newToken = await refreshPromise;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);

// ========== 认证 ==========
export const authAPI = {
  register: (data: {
    email: string; password: string; username?: string;
    phone?: string; real_name: string; organization_name?: string;
    tenant_id?: string; create_new_tenant?: boolean;
    invite_code?: string; tenant_invitation_token?: string;
  }) =>
    api.post<AuthActionResponse>('/api/auth/register', data).then(r => r.data),

  searchOrganizations: (query: string) =>
    api.get<{
      items: Array<{
        tenant_id: string;
        name: string;
        tenant_code: string;
        member_count: number;
        created_at?: string | null;
        exact_match: boolean;
        status: string;
      }>;
    }>('/api/tenants/search', { params: { q: query } }).then(r => r.data),

  verifyEmail: (data: { email?: string; code?: string; token?: string }) =>
    api.post<AuthActionResponse>('/api/auth/verify-email', data).then(r => r.data),

  resendVerification: (email: string) =>
    api.post<AuthActionResponse>('/api/auth/resend-verification', { email }).then(r => r.data),

  forgotPassword: (email: string) =>
    api.post<AuthActionResponse>('/api/auth/forgot-password', { email }).then(r => r.data),

  resetPassword: (data: { token: string; new_password: string }) =>
    api.post<AuthActionResponse>('/api/auth/reset-password', data).then(r => r.data),

  login: (data: { email: string; password: string }) =>
    api.post<TokenResponse>('/api/auth/login', data).then(r => r.data),

  refresh: (refreshToken: string) =>
    api.post<TokenResponse>('/api/auth/refresh', { refresh_token: refreshToken }).then(r => r.data),

  exchangeOAuthTicket: (ticket: string) =>
    api.post<TokenResponse>('/api/oauth/exchange', { ticket }).then(r => r.data),

  logout: () => api.post('/api/auth/logout').then(r => r.data),

  me: () => api.get<User>('/api/auth/me').then(r => r.data),

  accessState: () => api.get<{
    membership_status: 'approved' | 'pending' | 'rejected' | 'none';
    can_enter_workspace: boolean;
    pending_join_requests: Array<{ tenant_name?: string; review_note?: string | null }>;
    rejected_join_requests: Array<{ tenant_name?: string; review_note?: string | null }>;
  }>('/api/auth/access-state').then(r => r.data),

  updateMe: (data: { username?: string; phone?: string; real_name?: string; avatar_url?: string }) =>
    api.patch<User>('/api/auth/me', data).then(r => r.data),

  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post('/api/auth/change-password', data).then(r => r.data),
};

export const patientAPI = {
  create: (data: { patient_id: string; birth_date?: string; gender?: string }) =>
    api.post<Patient>('/api/patients/', data).then(r => r.data),

  list: (skip = 0, limit = 50) =>
    api.get<Patient[]>('/api/patients/', { params: { skip, limit } }).then(r => r.data),

  get: (id: string) =>
    api.get<Patient>(`/api/patients/${id}`).then(r => r.data),

  search: (patientCode: string) =>
    api.get<Patient>(`/api/patients/search/${patientCode}`).then(r => r.data),

  update: (id: string, data: { birth_date?: string; gender?: string }) =>
    api.put<Patient>(`/api/patients/${id}`, data).then(r => r.data),
};

// ========== 病例管理 ==========
export const caseAPI = {
  create: (data: {
    patient_id: string;
    drug_name: string;
    diagnosis?: string;
    covariates: Record<string, any>;
    ai_extraction_snapshot?: Record<string, any>;
  }) => api.post<Case>('/api/cases/', data).then(r => r.data),

  list: (patientId?: string, status?: string, params?: { page?: number; page_size?: number; search?: string; drug_name?: string }) =>
    api.get<Case[] | { items: Case[]; total: number; page: number; page_size: number }>('/api/cases/', { params: { patient_id: patientId, status: status || 'active', ...params } }).then(r => r.data),

  get: (id: string) =>
    api.get<Case>(`/api/cases/${id}`).then(r => r.data),

  history: (params?: { search?: string; drug_name?: string; status?: string; start_date?: string; end_date?: string; page?: number; page_size?: number }) =>
    api.get<Case[] | { items: Case[]; total: number; page: number; page_size: number }>('/api/cases/history', { params }).then(r => r.data),

  stats: () => api.get<{ active_count: number; archived_count: number; closed_count: number; total_count: number }>('/api/cases/stats').then(r => r.data),

  archive: (id: string) =>
    api.patch(`/api/cases/${id}/archive`).then(r => r.data),

  reactivate: (id: string) =>
    api.patch(`/api/cases/${id}/reactivate`).then(r => r.data),

  update: (
    id: string,
    data: {
      diagnosis?: string;
      covariates?: Record<string, any>;
      ai_extraction_snapshot?: Record<string, any>;
      status?: string;
    },
  ) =>
    api.put<Case>(`/api/cases/${id}`, data).then(r => r.data),
};

export const modelAPI = {
  match: (patientInfo: Record<string, any>) =>
    api.post<ModelMatchResponse>('/api/models/match', patientInfo).then(r => r.data),

  startMatch: (patientInfo: Record<string, any>) =>
    api.post<ModelMatchTaskResponse>('/api/models/match/start', patientInfo).then(r => r.data),

  getMatchTask: (taskId: string) =>
    api.get<ModelMatchTaskResponse>(`/api/models/match/status/${taskId}`).then(r => r.data),

  list: (drugName?: string, administrationMode?: AdministrationMode) => {
    const params = new URLSearchParams();
    if (drugName) params.set('drug_name', drugName);
    if (administrationMode) params.set('administration_mode', administrationMode);
    const query = params.toString();
    return api.get<{ models: ModelListItem[]; total: number }>(`/api/models/list${query ? `?${query}` : ''}`).then(r => r.data);
  },

  getDetail: (modelId: string) =>
    api.get<Record<string, any>>(`/api/models/${modelId}`).then(r => r.data),

  getValidation: (modelId: string) =>
    api.get<Record<string, any>>(`/api/models/${encodeURIComponent(modelId)}/validation`).then(r => r.data),

  /** 获取每个药物（中文名）的聚合人群标签，仅 active 模型 */
  drugPopulations: () =>
    api.get<Record<string, string[]>>('/api/models/drug-populations').then(r => r.data),
};

// ========== 剂量计算 ==========

type OptimizeDosePayload = {
  case_id: string;
  model_id: string;
  administration_mode?: AdministrationMode;
  future_infusion_duration_h?: number;
  tdm_data: Array<{
    concentration: number;
    sampling_time?: string;
    sample_time?: string;
    time_after_dose: number;
    dose: number;
    administration_mode?: AdministrationMode;
    infusion_duration?: number;
    dosing_interval?: number;
    n_doses?: number;
    regimen_index?: number;
    absolute_time_h?: number;
  }>;
  dosing_regimens?: Array<{
    dose: number;
    administration_mode?: AdministrationMode;
    dosing_interval?: number;
    infusion_duration?: number;
    dosing_start_time?: string;
    n_doses?: number;
    n_doses_manual?: boolean;
    n_doses_source?: 'auto' | 'manual';
  }>;
  target_auc?: number;
  target_type?: string;
  target_value?: number;
  mic?: number;
  regimen_profile_id?: string;
};

type InterpretationRegimenPayload = {
  selected_regimen_active?: boolean;
  dose?: number;
  dose_mg?: number;
  recommended_dose_mg?: number;
  dose_kg?: number;
  dose_mg_kg?: number;
  recommended_dose_mg_kg?: number;
  interval?: number;
  interval_h?: number;
  dosing_interval_h?: number;
  infusion_h?: number;
  infusion_duration_h?: number;
  auc24?: number;
  predicted_auc24?: number;
  strategy_type?: string;
  transition_time_h?: number;
  recommended_loading_dose_mg?: number;
  recommended_loading_dose_mg_kg?: number;
  loading_regimen?: {
    enabled?: boolean;
    dose_mg?: number;
    dose_mg_kg?: number;
    count?: number;
  };
  maintenance_regimen?: {
    dose_mg?: number;
    dose_mg_kg?: number;
    interval_h?: number;
  };
  selected_candidate?: RemedialCandidate;
};

export const doseAPI = {
  calculateInitial: (data: {
    case_id: string;
    model_id: string;
    administration_mode?: AdministrationMode;
    infusion_duration_h?: number;
    target_auc?: number;
    target_type?: string;
    target_value?: number;
    mic?: number;
    loading_strategy?: Record<string, any>;
    regimen_profile_id?: string;
  }) => api.post<DoseResult>('/api/dose/initial', data, { timeout: PK_CALCULATION_TIMEOUT }).then(r => r.data),

  optimize: (data: {
    case_id: string;
    model_id: string;
    administration_mode?: AdministrationMode;
    future_infusion_duration_h?: number;
    tdm_data: Array<{
      concentration: number;
      sampling_time?: string;
      sample_time?: string;
      time_after_dose: number;
      dose: number;
      administration_mode?: AdministrationMode;
      infusion_duration?: number;
      dosing_interval?: number;
      n_doses?: number;
      regimen_index?: number;
      absolute_time_h?: number;
    }>;
    dosing_regimens?: Array<{
      dose: number;
      administration_mode?: AdministrationMode;
      dosing_interval?: number;
      infusion_duration?: number;
      dosing_start_time?: string;
      n_doses?: number;
      n_doses_manual?: boolean;
      n_doses_source?: 'auto' | 'manual';
    }>;
    target_auc?: number;
    target_type?: string;
    target_value?: number;
    mic?: number;
    regimen_profile_id?: string;
  }) => api.post<DoseResult>('/api/dose/optimize', data, { timeout: PK_CALCULATION_TIMEOUT }).then(r => r.data),

  startOptimize: (data: OptimizeDosePayload) =>
    api.post<{
      task_id: string;
      status: 'queued' | 'running' | 'completed' | 'failed' | string;
      progress_percent: number;
      stage: string;
      message?: string | null;
      started_at: string;
      updated_at: string;
      result?: DoseResult | null;
      error?: string | null;
    }>('/api/dose/optimize/start', data).then(r => r.data),

  getOptimizeTask: (taskId: string) =>
    api.get<{
      task_id: string;
      status: 'queued' | 'running' | 'completed' | 'failed' | string;
      progress_percent: number;
      stage: string;
      message?: string | null;
      started_at: string;
      updated_at: string;
      result?: DoseResult | null;
      error?: string | null;
    }>(`/api/dose/optimize/status/${taskId}`).then(r => r.data),

  pkFormula: (data: {
    case_id: string;
    administration_mode?: AdministrationMode;
    dose: number;
    measured_peak?: number;
    measured_trough?: number;
    t_peak?: number;
    t_trough?: number;
    infusion_duration: number;
    infusion_duration_source: 'user_input' | 'drug_config_default';
    dosing_interval: number;
    weight: number;
    target_auc?: number;
    target_type?: string;
    target_value?: number;
    mic?: number;
    regimen_profile_id?: string;
    inputs?: Record<string, unknown>;
  }) => api.post<PKFormulaResult>('/api/dose/pk-formula', data).then(r => r.data),

  /** Recalculate a user-edited Sanford candidate with saved individual PK parameters. */
  recalcPkFormulaRegimen: (data: {
    calculation_id: string;
    dose_mg: number;
    interval_h: number;
    infusion_h: number;
    target_type?: string;
    target_auc?: number;
    mic?: number;
  }) => api.post<GridSearchRegimen & {
    simulation_data?: { time: number[]; concentration: number[] };
    pta_status?: string;
    pta_reason?: string;
  }>('/api/dose/pk-formula/recalc-regimen', data, { timeout: PK_CALCULATION_TIMEOUT }).then(r => r.data),

  remedial: (data: {
    case_id: string;
    model_id: string;
    current_regimen: { dose_mg: number; interval_h: number; infusion_h?: number };
    scheduled_time: string;
    noticed_time: string;
    administration_mode?: AdministrationMode;
    risk_preference?: 'balanced' | 'bleeding' | 'thrombosis';
    weights?: { high?: number; low?: number; simple?: number };
    prefer_simple_doses?: boolean;
    return_top_n?: number;
  }) => api.post<RemedialDoseResult>(
    '/api/dose/remedial',
    data,
    { timeout: PK_CALCULATION_TIMEOUT },
  ).then(r => r.data),

  /** Re-simulate one manually adjusted remedial candidate from its source calculation snapshot. */
  remedialCustomCandidate: (data: {
    calculation_id: string;
    base_candidate_id: string;
    immediate_dose_mg: number;
    next_dose_mg: number;
  }) => api.post<{
    calculation_id: string;
    candidate: RemedialCandidate;
    simulation_runtime?: Record<string, unknown> | null;
    validation_flags?: string[];
    validation_messages?: string[];
  }>(
    '/api/dose/remedial/custom-candidate',
    data,
    { timeout: PK_CALCULATION_TIMEOUT },
  ).then(r => r.data),

  /** Rebuild the clinical interpretation for the selected regimen. */
  interpret: (
    calculationId: string,
    selectedRegimen: InterpretationRegimenPayload,
    selectedMethod?: string,
  ) =>
    api.post<{ clinical_interpretation: string }>('/api/dose/interpret', {
      calculation_id: calculationId,
      selected_regimen: selectedRegimen,
      selected_method: selectedMethod,
    }).then(r => r.data.clinical_interpretation),

  recalcRegimen: (data: {
    case_id: string;
    model_id: string;
    dose_mg: number;
    interval_h: number;
    selected_method?: string;
    administration_mode?: AdministrationMode;
    tdm_data?: Array<any>;
    dosing_regimens?: Array<any>;
    infusion_h?: number;
    infusion_duration_source?: 'user_input' | 'drug_config_default' | 'administration_mode';
    mic?: number;
    mic_source?: 'user_input' | 'drug_config_default';
    n_sim_doses?: number;
    pta_context?: Record<string, any>;
    map_parameters?: Record<string, any>;
    loading_strategy?: Record<string, any>;
    curve_only?: boolean;
  }) => api.post<{
    dose_mg: number;
    dose_mg_kg: number | null;
    dose_mg_m2?: number | null;
    dose_basis?: 'mg_kg' | 'mg' | 'mg_m2';
    interval_h: number;
    infusion_h?: number;
    infusion_duration_h?: number;
    infusion_duration_source?: 'user_input' | 'drug_config_default' | 'administration_mode';
    mic?: number;
    mic_source?: 'user_input' | 'drug_config_default';
    administration_mode?: AdministrationMode;
    maintenance_daily_dose_mg?: number | null;
    maintenance_rate_mg_h?: number | null;
    predicted_css?: number | null;
    recommended_loading_dose_mg?: number | null;
    recommended_loading_dose_mg_kg?: number | null;
    recommended_loading_dose_mg_m2?: number | null;
    predicted_cmin: number | null;
    predicted_cmax: number | null;
    pta: number | null;
    is_optimal: boolean;
    pk_parameters: Record<string, number>;
    pkpd_results: Record<string, any>;
    simulation_data?: {
      time: number[];
      concentration: number[];
      concentration_p025?: number[];
      concentration_p975?: number[];
      concentration_p05?: number[];
      concentration_p95?: number[];
      n_subjects?: number;
    };
    combined_curve?: Record<string, any>;
    tdm_points?: Array<{ time: number; concentration: number; label?: string }>;
    regimen_history?: Array<Record<string, any>>;
    safety_check?: SafetyCheckResult;
    pta_context?: Record<string, any>;
    pta_meta?: {
      seed: number;
      n_sim: number;
      omega_source: string;
      pk_source: string;
    };
    curve_only?: boolean;
  }>('/api/dose/recalc-regimen', data, { timeout: PK_CALCULATION_TIMEOUT }).then(r => r.data),
};

export const intakeAPI = {
  context: (data: {
    drug_id?: string;
    drug_name?: string;
    patient_id?: string;
    birth_date?: string;
    current_date?: string;
    covariates?: Record<string, any>;
    confirmed_model_id?: string;
    administration_mode?: AdministrationMode;
  }) =>
    api.post<IntakeContextResponse>('/api/intake/context', data).then(r => r.data),
};

// ========== 报告 ==========
export const reportAPI = {
  view: (reportId: string) =>
    api.get<string>(`/api/report/view/${reportId}`, { responseType: 'text' }).then(r => r.data),

  generate: (
    calculationId: string,
    customInterpretation?: string,
    selectedRegimen?: Record<string, any>,
    combinedCurve?: Record<string, any>,
    tdmPoints?: Array<Record<string, any>>,
    regimenHistory?: Array<Record<string, any>>,
    tdmData?: Array<Record<string, any>>,
  ) =>
    api.post<string>(
      `/api/report/generate/${calculationId}`,
      {
        custom_interpretation: customInterpretation || null,
        selected_regimen: selectedRegimen || null,
        combined_curve: combinedCurve || null,
        tdm_points: tdmPoints || null,
        regimen_history: regimenHistory || null,
        tdm_data: tdmData || null,
      },
      { responseType: 'text' },
    ).then(r => r.data),

  sendEmail: (
    calculationId: string,
    toEmail: string,
    subject?: string,
    customInterpretation?: string,
    selectedRegimen?: Record<string, any>,
    combinedCurve?: Record<string, any>,
    tdmPoints?: Array<Record<string, any>>,
    regimenHistory?: Array<Record<string, any>>,
    tdmData?: Array<Record<string, any>>,
  ) =>
    api.post(`/api/report/send-email/${calculationId}`, {
      to_email: toEmail,
      subject: subject || null,
      custom_interpretation: customInterpretation || null,
      selected_regimen: selectedRegimen || null,
      combined_curve: combinedCurve || null,
      tdm_points: tdmPoints || null,
      regimen_history: regimenHistory || null,
      tdm_data: tdmData || null,
    }).then(r => r.data),

  history: (caseId: string) =>
    api.get<{ calculations: CalculationRecord[] }>(`/api/report/history/${caseId}`).then(r => r.data),
};

const parseSSEEvent = (chunk: string): { event: string; data: any } | null => {
  const lines = chunk.split(/\r?\n/);
  let event = 'message';
  const dataLines: string[] = [];

  lines.forEach((line) => {
    if (!line || line.startsWith(':')) return;
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
      return;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  });

  if (!dataLines.length) return null;
  return {
    event,
    data: JSON.parse(dataLines.join('\n')),
  };
};

// ========== AI 助手 ==========
export const aiAPI = {
  extract: (text: string, drugName?: string, populationGroup?: string) =>
    api.post<AIExtractResponse>('/api/ai/extract', {
      text,
      drug_name: drugName,
      population_group: populationGroup,
    }, {
      timeout: AI_REQUEST_TIMEOUT,
    }).then(r => r.data),

  assistantTurn: (data: {
    text: string;
    drug_name?: string;
    population_group?: string;
    current_fields?: Record<string, any>;
    current_field_meta?: Record<string, { source: AgentFieldSource }>;
    requested_action?: AgentRequestedAction;
    conversation_id?: string;
    case_context?: {
      tdm_data?: Array<Record<string, any>>;
      calc_type?: string;
      target_params?: Record<string, any>;
      model_id?: string;
      calc_result?: Record<string, any>;
      provider?: string;
    };
  }) =>
    api.post<AIAssistantTurnResponse>('/api/ai/assistant-turn', data, {
      timeout: AI_REQUEST_TIMEOUT,
    }).then(r => r.data),

  assistantTurnStream: async (
    data: {
      text: string;
      drug_name?: string;
      population_group?: string;
      current_fields?: Record<string, any>;
      current_field_meta?: Record<string, { source: AgentFieldSource }>;
      requested_action?: AgentRequestedAction;
      conversation_id?: string;
      case_context?: {
        tdm_data?: Array<Record<string, any>>;
        calc_type?: string;
        target_params?: Record<string, any>;
        model_id?: string;
        calc_result?: Record<string, any>;
        provider?: string;
      };
    },
    handlers?: {
      onStatus?: (payload: AIAssistantStreamStatusPayload) => void;
      onBaseResult?: (payload: AIAssistantTurnResponse) => void;
      onMatchedModel?: (payload: { model_id: string; model_name?: string }) => void;
      onFieldExtracted?: (payload: Record<string, any>) => void;
      onFieldConfirmed?: (payload: Record<string, any>) => void;
      onFieldConflict?: (payload: Record<string, any>) => void;
      onCandidateModelsUpdated?: (payload: Record<string, any>) => void;
      onBlockingFieldsUpdated?: (payload: Record<string, any>) => void;
      onCovariateRequirementsUpdated?: (payload: Record<string, any>) => void;
      onReadyToConfirm?: (payload: Record<string, any>) => void;
      onResult?: (payload: AIAssistantTurnResponse) => void;
    },
  ) => {
    const tenantId = localStorage.getItem('precidose_tenant_id');
    const openStream = async (accessToken?: string | null) => fetch(`${API_BASE}/api/ai/assistant-turn-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
      },
      body: JSON.stringify(data),
    });

    let response = await openStream(getStoredToken());
    if (response.status === 401 && getStoredRefresh()) {
      const refreshResponse = await axios.post<TokenResponse>(`${API_BASE}/api/auth/refresh`, {
        refresh_token: getStoredRefresh(),
      });
      storeTokens(refreshResponse.data.access_token, refreshResponse.data.refresh_token);
      response = await openStream(refreshResponse.data.access_token);
    }

    if (!response.ok) {
      const rawText = await response.text();
      let detail = 'AI 请求失败';
      try {
        const parsed = JSON.parse(rawText);
        detail = parsed?.detail || detail;
      } catch {
        detail = rawText || detail;
      }
      const error: any = new Error(detail);
      error.response = { status: response.status, data: { detail } };
      throw error;
    }

    if (!response.body) {
      throw new Error('当前环境不支持流式响应');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let finalResult: AIAssistantTurnResponse | null = null;

    while (true) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });

      let delimiterIndex = buffer.indexOf('\n\n');
      while (delimiterIndex !== -1) {
        const rawEvent = buffer.slice(0, delimiterIndex).trim();
        buffer = buffer.slice(delimiterIndex + 2);
        delimiterIndex = buffer.indexOf('\n\n');
        if (!rawEvent) continue;

        const parsed = parseSSEEvent(rawEvent);
        if (!parsed) continue;

        if (parsed.event === 'status') {
          handlers?.onStatus?.(parsed.data as AIAssistantStreamStatusPayload);
        } else if (parsed.event === 'base_result') {
          handlers?.onBaseResult?.(parsed.data as AIAssistantTurnResponse);
        } else if (parsed.event === 'matched_model') {
          handlers?.onMatchedModel?.(parsed.data as { model_id: string; model_name?: string });
        } else if (parsed.event === 'field_extracted') {
          handlers?.onFieldExtracted?.(parsed.data as Record<string, any>);
        } else if (parsed.event === 'field_confirmed') {
          handlers?.onFieldConfirmed?.(parsed.data as Record<string, any>);
        } else if (parsed.event === 'field_conflict') {
          handlers?.onFieldConflict?.(parsed.data as Record<string, any>);
        } else if (parsed.event === 'candidate_models_updated') {
          handlers?.onCandidateModelsUpdated?.(parsed.data as Record<string, any>);
        } else if (parsed.event === 'blocking_fields_updated') {
          handlers?.onBlockingFieldsUpdated?.(parsed.data as Record<string, any>);
        } else if (parsed.event === 'covariate_requirements_updated') {
          handlers?.onCovariateRequirementsUpdated?.(parsed.data as Record<string, any>);
        } else if (parsed.event === 'ready_to_confirm') {
          handlers?.onReadyToConfirm?.(parsed.data as Record<string, any>);
        } else if (parsed.event === 'result') {
          finalResult = parsed.data as AIAssistantTurnResponse;
          handlers?.onResult?.(finalResult);
        } else if (parsed.event === 'error') {
          const detail = parsed.data?.detail || 'AI 流式请求失败';
          const error: any = new Error(detail);
          error.response = { status: response.status, data: { detail } };
          throw error;
        }
      }

      if (done) break;
    }

    if (!finalResult) {
      throw new Error('AI 未返回最终结果');
    }

    return finalResult;
  },

  chat: (messages: Array<{ role: string; content: string }>, patientContext?: any) =>
    api.post<{ reply: string }>('/api/ai/chat', { messages, patient_context: patientContext }, {
      timeout: AI_REQUEST_TIMEOUT,
    }).then(r => r.data),
};

// ========== MIPD Agent ==========
export const agentAPI = {
  analyze: (data: {
    patient_context: Record<string, any>;
    model_id: string;
    target_auc?: number;
    tdm_data?: Array<{ time: number; concentration: number; infusion_duration?: number; dose_mg?: number }>;
    drug_name?: string;
    drug_config?: Record<string, any>;
    calc_result?: Record<string, any>;
    provider?: string;
  }) => api.post<{
    report: string;
    recommendation: Record<string, any>;
    candidates: Array<Record<string, any>>;
    unit_context?: Record<string, any>;
    tool_calls_log: Array<Record<string, any>>;
    provider: string;
    model: string;
    agent_used: boolean;
    fallback_reason?: string;
    matched_model_id?: string;
  }>('/api/agent/analyze', data, { timeout: 120000 }).then(r => r.data),

  converse: (data: {
    session_id?: string;
    message: string;
    business_context?: Record<string, any>;
    drug_config?: Record<string, any>;
    provider?: string;
  }) => api.post<{
    session_id: string;
    reply: string;
    status: 'collecting' | 'analyzing' | 'complete';
    collected_params: Record<string, any>;
    analysis_result?: Record<string, any>;
    candidates: Array<Record<string, any>>;
  }>('/api/agent/converse', data, { timeout: 120000 }).then(r => r.data),
};

export const healthAPI = {
  check: () => api.get('/health').then(r => r.data),
};

// ========== 药物参数管理 ==========
export const drugAPI = {
  list: () =>
    api.get<{ drugs: any[]; total: number }>('/api/drugs/list').then(r => r.data),

  get: (drugId: string) =>
    api.get<any>(`/api/drugs/${drugId}`).then(r => r.data),

  /** 获取当前工作区的脱敏药物能力目录 */
  catalog: () =>
    api.get<{ drugs: DrugCatalogItem[]; total: number }>('/api/drugs/catalog').then(r => r.data),

  /** 获取单个药物的脱敏能力详情（不含模型适用边界和可选协变量） */
  capabilities: (drugId: string) =>
    api.get<DrugCapabilityResponse>(`/api/drugs/${encodeURIComponent(drugId)}/capabilities`).then(r => r.data),

  /** 获取当前租户已激活的药物 */
  activated: () =>
    api.get<{ drugs: any[]; total: number; activated_ids: string[]; needs_activation: boolean }>(
      '/api/drugs/activated'
    ).then(r => r.data),

  /** 获取药物激活状态（含套餐限制信息） */
  activationStatus: () =>
    api.get<{
      all_drugs: Array<{ drug_id: string; drug_name: string; drug_name_en?: string; category?: string; activated: boolean }>;
      activated_ids: string[];
      multi_drug: boolean;
      max_drugs: number;
      max_models: number;
      needs_activation: boolean;
      activation_scope?: 'tenant' | 'user';
      user_override?: boolean;
      can_manage_activation?: boolean;
    }>('/api/drugs/activation-status').then(r => r.data),

  activate: (drugIds: string[]) =>
    api.post<{ activated_drugs: string[]; message: string }>(
      '/api/drugs/activate', { drug_ids: drugIds }
    ).then(r => r.data),
};

// ========== 租户统计 ==========
export const tenantStatsAPI = {
  getStats: (tenantId: string, params?: any) =>
    api.get(`/api/tenants/${tenantId}/stats`, { params }).then(r => r.data),
  exportExcel: (tenantId: string, params?: any) =>
    api.get(`/api/tenants/${tenantId}/stats/export`, { params, responseType: 'blob' }),
};

// ========== 发票管理 ==========
export const invoiceAPI = {
  list: () => api.get('/api/invoices/').then(r => r.data),
  apply: (data: any) => api.post('/api/invoices/', data).then(r => r.data),
  get: (id: string) => api.get(`/api/invoices/${id}`).then(r => r.data),
};

export const adminAuthAPI = {
  login: (data: { email: string; password: string }) =>
    adminAxios.post('/api/admin/login', data).then(r => r.data),
  me: () => adminAxios.get('/api/admin/me').then(r => r.data),
};

export const adminAPI = {
  // 统计
  stats: () => adminAxios.get('/api/admin/stats').then(r => r.data),

  // 用户管理
  listUsers: (skip = 0, limit = 50, search?: string) =>
    adminAxios.get('/api/admin/users', { params: { skip, limit, search } }).then(r => r.data),
  createUser: (data: Record<string, any>) =>
    adminAxios.post('/api/admin/users', data).then(r => r.data),
  getUserDetail: (userId: string) =>
    adminAxios.get(`/api/admin/users/${userId}`).then(r => r.data),
  updateUser: (userId: string, data: Record<string, any>) =>
    adminAxios.patch(`/api/admin/users/${userId}`, data).then(r => r.data),
  sendUserEmail: (userId: string, data: { subject: string; content: string }) =>
    adminAxios.post(`/api/admin/users/${userId}/send-email`, data).then(r => r.data),
  resetPassword: (userId: string, newPassword: string) =>
    adminAxios.post(`/api/admin/users/${userId}/reset-password`, { new_password: newPassword }).then(r => r.data),

  // 租户管理
  listTenants: (skip = 0, limit = 50, search?: string, status?: string) =>
    adminAxios.get('/api/admin/tenants', { params: { skip, limit, search, status } }).then(r => r.data),
  getTenantDetail: (tenantId: string) =>
    adminAxios.get(`/api/admin/tenants/${tenantId}`).then(r => r.data),
  updateTenant: (tenantId: string, data: Record<string, any>) =>
    adminAxios.patch(`/api/admin/tenants/${tenantId}`, data).then(r => r.data),
  deleteTenant: (tenantId: string, confirmationSlug: string) =>
    adminAxios.delete(`/api/admin/tenants/${tenantId}`, {
      data: { confirmation_slug: confirmationSlug },
    }).then(r => r.data),
  updateTenantOwner: (tenantId: string, userId: string) =>
    adminAxios.patch(`/api/admin/tenants/${tenantId}/owner`, { user_id: userId }).then(r => r.data),
  previewTenantMerge: (sourceTenantId: string, targetTenantId: string) =>
    adminAxios.post('/api/admin/tenant-merges/preview', {
      source_tenant_id: sourceTenantId,
      target_tenant_id: targetTenantId,
    }).then(r => r.data),
  executeTenantMerge: (data: {
    job_id: string;
    source_confirmation_slug: string;
    target_confirmation_slug: string;
    patient_resolutions?: Array<Record<string, unknown>>;
  }) => adminAxios.post('/api/admin/tenant-merges/execute', data).then(r => r.data),
  authorizeTenantMember: (tenantId: string, data: { user_id: string; role: string; is_default?: boolean }) =>
    adminAxios.post(`/api/admin/tenants/${tenantId}/members`, data).then(r => r.data),
  updateTenantMemberAuthorization: (tenantId: string, userId: string, data: { role?: string; is_default?: boolean }) =>
    adminAxios.patch(`/api/admin/tenants/${tenantId}/members/${userId}`, data).then(r => r.data),
  revokeTenantMember: (tenantId: string, userId: string) =>
    adminAxios.delete(`/api/admin/tenants/${tenantId}/members/${userId}`).then(r => r.data),
  listTenantJoinRequests: (status = 'pending') =>
    adminAxios.get('/api/admin/tenant-join-requests', { params: { status } }).then(r => r.data),
  decideTenantJoinRequest: (requestId: string, data: { action: 'approve' | 'reject'; note?: string }) =>
    adminAxios.post(`/api/admin/tenant-join-requests/${requestId}/decision`, data).then(r => r.data),
  relocateTenantMember: (tenantId: string, userId: string, data: {
    target_tenant_id?: string;
    new_tenant_name?: string;
    confirm_data_stays_with_source: boolean;
  }) => adminAxios.post(`/api/admin/tenants/${tenantId}/members/${userId}/relocate`, data).then(r => r.data),
  listTenantSubs: (tenantId: string) =>
    adminAxios.get(`/api/admin/tenants/${tenantId}/subscriptions`).then(r => r.data),
  updateTenantSub: (tenantId: string, subId: string, data: Record<string, any>) =>
    adminAxios.patch(`/api/admin/tenants/${tenantId}/subscriptions/${subId}`, data).then(r => r.data),

  // 药物配置
  listDrugConfigs: () => adminAxios.get('/api/admin/drugs').then(r => r.data),
  createDrugConfig: (data: any) => adminAxios.post('/api/admin/drugs', data).then(r => r.data),
  updateDrugConfig: (drugId: string, data: any) => adminAxios.put(`/api/admin/drugs/${drugId}`, data).then(r => r.data),
  deleteDrugConfig: (drugId: string) => adminAxios.delete(`/api/admin/drugs/${drugId}`).then(r => r.data),
  getDrugActivationAssignments: () =>
    adminAxios.get('/api/admin/drug-activation').then(r => r.data),
  updateTenantDrugActivation: (tenantId: string, drugIds: string[]) =>
    adminAxios.put(`/api/admin/drug-activation/tenants/${tenantId}`, { drug_ids: drugIds }).then(r => r.data),
  updateUserDrugActivation: (userId: string, data: { drug_ids?: string[]; inherit_tenant?: boolean }) =>
    adminAxios.put(`/api/admin/drug-activation/users/${userId}`, data).then(r => r.data),
  getModelActivationAssignments: () =>
    adminAxios.get('/api/admin/model-activation').then(r => r.data),
  updateTenantModelActivation: (tenantId: string, modelIds: string[]) =>
    adminAxios.put(`/api/admin/model-activation/tenants/${tenantId}`, { model_ids: modelIds }).then(r => r.data),
  updateUserModelActivation: (userId: string, data: { model_ids?: string[]; inherit_tenant?: boolean }) =>
    adminAxios.put(`/api/admin/model-activation/users/${userId}`, data).then(r => r.data),

  // 模型审核
  listPendingModels: () => adminAxios.get('/api/admin/models/pending').then(r => r.data),
  listAllModels: (params?: Record<string, any>) =>
    adminAxios.get('/api/admin/models', { params }).then(r => r.data),
  reviewModel: (modelId: string, data: { review_status: string; review_note?: string; qualification_run_id?: string }) =>
    adminAxios.post(`/api/admin/models/${modelId}/review`, data).then(r => r.data),
  toggleModelStatus: (modelId: string, modelStatus: string) =>
    adminAxios.patch(`/api/admin/models/${modelId}/status`, { model_status: modelStatus }).then(r => r.data),
  updateModelPublication: (modelId: string, publicationState: 'published' | 'unpublished', reason?: string) =>
    adminAxios.patch(`/api/models/${encodeURIComponent(modelId)}/publication`, { publication_state: publicationState, reason }).then(r => r.data),
  deleteModel: (modelId: string) =>
    adminAxios.delete(`/api/admin/models/${modelId}`).then(r => r.data),

  listModelsMeta: (drugName?: string) =>
    adminAxios.get<AdminModelListResponse>(
      '/api/admin/models/list-meta',
      { params: { drug_name: drugName } },
    ).then(r => r.data),
  getModelDetail: (modelId: string) =>
    adminAxios.get(`/api/admin/models/${encodeURIComponent(modelId)}/detail`).then(r => r.data),
  updateModelDetail: (modelId: string, data: Record<string, any>) =>
    adminAxios.put(`/api/admin/models/${encodeURIComponent(modelId)}/detail`, data).then(r => r.data),
  getModelScriptContent: (modelId: string) =>
    adminAxios.get(`/api/admin/models/${encodeURIComponent(modelId)}/scripts/content`).then(r => r.data),
  updateModelScriptContent: (modelId: string, data: Record<string, any>) =>
    adminAxios.put(`/api/admin/models/${encodeURIComponent(modelId)}/scripts/content`, data).then(r => r.data),
  getPopulationTags: () =>
    adminAxios.get('/api/admin/models/population-tags').then(r => r.data),

  listValidationScriptReports: (params?: Record<string, any>) =>
    adminAxios.get('/api/model-validation/script-reports', { params }).then(r => r.data),
  runModelScriptValidation: (modelId: string, scriptFileBase?: string) =>
    adminAxios.post(
      `/api/model-validation/script-check/${encodeURIComponent(modelId)}`,
      undefined,
      { params: { script_file_base: scriptFileBase } },
    ).then(r => r.data),
  listValidationDatasets: (params?: Record<string, any>) =>
    adminAxios.get('/api/model-validation/datasets', { params }).then(r => r.data),
  createValidationDataset: (data: Record<string, any>) =>
    adminAxios.post('/api/model-validation/datasets', data).then(r => r.data),
  generateValidationSyntheticDataset: (data: Record<string, unknown>) =>
    adminAxios.post('/api/model-validation/synthetic-datasets/generate', data, {
      timeout: SYNTHETIC_DATASET_TIMEOUT,
    }).then(r => r.data),
  getValidationDataset: (datasetId: string) =>
    adminAxios.get(`/api/model-validation/datasets/${datasetId}`).then(r => r.data),
  deleteValidationDataset: (datasetId: string) =>
    adminAxios.delete(`/api/model-validation/datasets/${datasetId}`).then(r => r.data),
  downloadValidationDatasetTemplate: (format: 'xlsx' | 'csv') =>
    adminAxios.get('/api/model-validation/datasets/template', {
      params: { format },
      responseType: 'blob',
    }).then(r => r.data),
  createValidationRun: (data: Record<string, any>) =>
    adminAxios.post('/api/model-validation/runs', data).then(r => r.data),
  listValidationRuns: (params?: Record<string, any>) =>
    adminAxios.get('/api/model-validation/runs', { params }).then(r => r.data),
  getValidationRun: (runId: string) =>
    adminAxios.get(`/api/model-validation/runs/${runId}`).then(r => r.data),
  cancelValidationRun: (runId: string) =>
    adminAxios.post(`/api/model-validation/runs/${runId}/cancel`).then(r => r.data),
  rerunValidationRun: (runId: string) =>
    adminAxios.post(`/api/model-validation/runs/${runId}/rerun`).then(r => r.data),
  deleteValidationRun: (runId: string) =>
    adminAxios.delete(`/api/model-validation/runs/${runId}`).then(r => r.data),
  getValidationRunResults: (runId: string) =>
    adminAxios.get(`/api/model-validation/runs/${runId}/results`).then(r => r.data),
  uploadValidationDataset: (data: FormData) =>
    adminAxios.post('/api/model-validation/datasets/upload', data).then(r => r.data),
  listValidationSampleDatasets: () =>
    adminAxios.get('/api/model-validation/sample-datasets').then(r => r.data),
  createValidationSampleDataset: (sampleKey: string) =>
    adminAxios.post(`/api/model-validation/sample-datasets/${encodeURIComponent(sampleKey)}/create`).then(r => r.data),
  listValidationClinicalCandidates: (params?: Record<string, any>) =>
    adminAxios.get('/api/model-validation/clinical-candidates', { params }).then(r => r.data),
  createValidationDatasetFromClinical: (data: Record<string, any>) =>
    adminAxios.post('/api/model-validation/datasets/from-clinical', data).then(r => r.data),
  updateModelValidationDrugName: (modelId: string, drugName: string) =>
    adminAxios.patch(`/api/model-validation/models/${encodeURIComponent(modelId)}/drug-name`, { drug_name: drugName }).then(r => r.data),
  inferModelValidationDrugName: (modelId: string) =>
    adminAxios.post(`/api/model-validation/models/${encodeURIComponent(modelId)}/infer-drug`).then(r => r.data),
  getModelValidationEvidence: (modelId: string) =>
    adminAxios.get<ModelValidationEvidenceResponse>(
      `/api/model-validation/models/${encodeURIComponent(modelId)}/validation-evidence`,
    ).then(r => r.data),

  uploadModelCandidatePackage: (file: File, expectedModelId?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (expectedModelId) formData.append('expected_model_id', expectedModelId);
    return adminAxios.post<ModelCandidatePackageUploadResponse>(
      '/api/model-validation/packages/upload',
      formData,
    ).then(r => r.data);
  },
  exportModelPackage: (modelId: string) =>
    adminAxios.get(
      `/api/model-validation/packages/models/${encodeURIComponent(modelId)}/export`,
      { responseType: 'blob' },
    ),
  uploadModelScriptUpgrade: (
    modelId: string,
    packageVersion: string,
    rFile: File,
    cppFile: File,
  ) => {
    const formData = new FormData();
    formData.append('model_id', modelId);
    formData.append('package_version', packageVersion);
    formData.append('r_file', rFile);
    formData.append('cpp_file', cppFile);
    return adminAxios.post<ModelCandidatePackageUploadResponse>(
      '/api/model-validation/packages/script-upgrade',
      formData,
    ).then(r => r.data);
  },
  listModelCandidatePackages: () =>
    adminAxios.get<ModelCandidatePackageListResponse>('/api/model-validation/packages').then(r => r.data),
  getModelCandidatePackage: (packageId: string) =>
    adminAxios.get<ModelCandidatePackage>(
      `/api/model-validation/packages/${encodeURIComponent(packageId)}`,
    ).then(r => r.data),
  deleteModelCandidatePackage: (packageId: string) =>
    adminAxios.delete(
      `/api/model-validation/packages/${encodeURIComponent(packageId)}`,
    ).then(r => r.data),
  withdrawModelCandidatePackage: (packageId: string) =>
    adminAxios.post<ModelCandidatePackage>(
      `/api/model-validation/packages/${encodeURIComponent(packageId)}/withdraw`,
    ).then(r => r.data),
  associateModelCandidatePackage: (packageId: string, modelId: string) =>
    adminAxios.post<ModelCandidatePackage>(
      `/api/model-validation/packages/${encodeURIComponent(packageId)}/associate`,
      { model_id: modelId },
    ).then(r => r.data),
  createModelDraftFromCandidatePackage: (packageId: string) =>
    adminAxios.post<ModelCandidatePackage>(
      `/api/model-validation/packages/${encodeURIComponent(packageId)}/create-model-draft`,
    ).then(r => r.data),
  qualifyModelCandidatePackage: (packageId: string) =>
    adminAxios.post<ModelQualificationRun>(
      `/api/model-validation/packages/${encodeURIComponent(packageId)}/qualify`,
      undefined,
      { timeout: MODEL_QUALIFICATION_TIMEOUT },
    ).then(r => r.data),
  listModelQualificationRuns: (packageId: string) =>
    adminAxios.get<ModelQualificationRunListResponse>(
      `/api/model-validation/packages/${encodeURIComponent(packageId)}/runs`,
    ).then(r => r.data),
  getModelQualificationRun: (runId: string) =>
    adminAxios.get<ModelQualificationRun>(
      `/api/model-validation/qualification-runs/${encodeURIComponent(runId)}`,
    ).then(r => r.data),
  updateModelDisplayName: (modelId: string, displayName: string) =>
    adminAxios.patch(
      `/api/model-validation/models/${encodeURIComponent(modelId)}/display-name`,
      { display_name: displayName },
    ).then(r => r.data),
  listModelReleases: (modelId: string) =>
    adminAxios.get<ModelReleaseListResponse>(
      `/api/models/${encodeURIComponent(modelId)}/releases`,
    ).then(r => r.data),
  selectModelRelease: (modelId: string, releaseId: string) =>
    adminAxios.post(
      `/api/models/${encodeURIComponent(modelId)}/releases/${encodeURIComponent(releaseId)}/select`,
    ).then(r => r.data),
  activateModel: (modelId: string, request: ModelActivationRequest) =>
    adminAxios.post<ModelActivationResponse>(
      `/api/models/${encodeURIComponent(modelId)}/activate`,
      request,
    ).then(r => r.data),
  publishQualifiedModel: (modelId: string, request: ModelPublicationRequest = {}) =>
    adminAxios.post<ModelPublicationResponse>(
      `/api/models/${encodeURIComponent(modelId)}/publish`,
      request,
    ).then(r => r.data),
  getModelWorkflow: (modelId: string, packageId?: string) =>
    adminAxios.get<ModelWorkflowProjection>(
      `/api/models/${encodeURIComponent(modelId)}/workflow`,
      { params: packageId ? { package_id: packageId } : undefined },
    ).then(r => r.data),
  preflightModelActivation: (modelId: string, request: ModelActivationRequest = {}) =>
    adminAxios.post<ModelActivationPreflightResponse>(
      `/api/models/${encodeURIComponent(modelId)}/activation-preflight`,
      request,
    ).then(r => r.data),

  listDrugParams: () =>
    adminAxios.get('/api/admin/drug-params').then(r => r.data),
  listPkFormulaStrategies: () =>
    adminAxios.get('/api/admin/pk-formula-strategies').then(r => r.data),
  getDrugParam: (drugId: string) =>
    adminAxios.get(`/api/admin/drug-params/${drugId}`).then(r => r.data),
  previewRegimenConfig: (drugId: string, data: { config?: Record<string, any>; patient_samples?: Array<Record<string, any>> }) =>
    adminAxios.post(`/api/admin/drug-params/${drugId}/regimen-preview`, data).then(r => r.data),
  updateDrugParam: (drugId: string, data: any) =>
    adminAxios.put(`/api/admin/drug-params/${drugId}`, data).then(r => r.data),
  createDrugParam: (data: any) =>
    adminAxios.post('/api/admin/drug-params', data).then(r => r.data),
  deleteDrugParam: (drugId: string) =>
    adminAxios.delete(`/api/admin/drug-params/${drugId}`).then(r => r.data),
  aiGenerateDrugConfig: (basicInfo: Record<string, any>) =>
    adminAxios.post('/api/admin/drug-params/ai-generate', basicInfo).then(r => r.data),
  getDrugOptions: () =>
    adminAxios.get('/api/admin/drug-params/options').then(r => r.data),
  listEvaluationRules: (params?: Record<string, any>) =>
    adminAxios.get('/api/admin/evaluation-rules', { params }).then(r => r.data),
  createEvaluationRule: (data: Record<string, any>) =>
    adminAxios.post('/api/admin/evaluation-rules', data).then(r => r.data),
  updateEvaluationRule: (id: number, data: Record<string, any>) =>
    adminAxios.put(`/api/admin/evaluation-rules/${id}`, data).then(r => r.data),
  publishEvaluationRule: (id: number) =>
    adminAxios.post(`/api/admin/evaluation-rules/${id}/publish`).then(r => r.data),
  archiveEvaluationRule: (id: number) =>
    adminAxios.post(`/api/admin/evaluation-rules/${id}/archive`).then(r => r.data),
  deleteEvaluationRule: (id: number) =>
    adminAxios.delete(`/api/admin/evaluation-rules/${id}`).then(r => r.data),
  previewEvaluationRules: (data: Record<string, any>) =>
    adminAxios.post('/api/admin/evaluation-rules/preview', data).then(r => r.data),

  // 站点配置
  getConfig: () => adminAxios.get('/api/admin/config').then(r => r.data),
  updateConfig: (key: string, value: any) =>
    adminAxios.put('/api/admin/config', { key, value }).then(r => r.data),

  // 发票管理
  listInvoices: (params?: any) => adminAxios.get('/api/admin/invoices', { params }).then(r => r.data),
  updateInvoiceStatus: (id: string, status: string) =>
    adminAxios.patch(`/api/admin/invoices/${id}`, { status }).then(r => r.data),

  // 公告
  listAnnouncements: () => adminAxios.get('/api/admin/announcements').then(r => r.data),
  createAnnouncement: (data: any) => adminAxios.post('/api/admin/announcements', data).then(r => r.data),
  updateAnnouncement: (id: string, data: any) => adminAxios.put(`/api/admin/announcements/${id}`, data).then(r => r.data),
  toggleAnnouncement: (id: string) => adminAxios.patch(`/api/admin/announcements/${id}`).then(r => r.data),
  deleteAnnouncement: (id: string) => adminAxios.delete(`/api/admin/announcements/${id}`).then(r => r.data),

  // 审计日志
  listAuditLogs: (params: Record<string, any>) =>
    adminAxios.get('/api/admin/audit-logs', { params }).then(r => r.data),

  workload: (params: Record<string, any>) =>
    adminAxios.get('/api/admin/workload', { params }).then(r => r.data),
  referralEvents: (params?: Record<string, any>) =>
    adminAxios.get('/api/admin/referral-events', { params }).then(r => r.data),
  exportWorkload: (params: Record<string, any>) =>
    adminAxios.get('/api/admin/workload/export', { params, responseType: 'blob' }).then(r => r),

  // 数据查询
  dataQuery: (params: Record<string, any>) =>
    adminAxios.get('/api/admin/data-query', { params }).then(r => r.data),
  replayOptimize: (data: OptimizeDosePayload) =>
    adminAxios.post<DoseResult>('/api/admin/data-query/replay-optimize', data, { timeout: PK_CALCULATION_TIMEOUT }).then(r => r.data),
  startReplayOptimize: (data: OptimizeDosePayload) =>
    adminAxios.post<{
      task_id: string;
      status: 'queued' | 'running' | 'completed' | 'failed' | string;
      progress_percent: number;
      stage: string;
      message?: string | null;
      started_at: string;
      updated_at: string;
      result?: DoseResult | null;
      error?: string | null;
    }>('/api/admin/data-query/replay-optimize/start', data).then(r => r.data),
  getReplayOptimizeTask: (taskId: string) =>
    adminAxios.get<{
      task_id: string;
      status: 'queued' | 'running' | 'completed' | 'failed' | string;
      progress_percent: number;
      stage: string;
      message?: string | null;
      started_at: string;
      updated_at: string;
      result?: DoseResult | null;
      error?: string | null;
    }>(`/api/admin/data-query/replay-optimize/status/${taskId}`).then(r => r.data),
  replayRecalcRegimen: (data: Parameters<typeof doseAPI.recalcRegimen>[0]) =>
    adminAxios.post<Awaited<ReturnType<typeof doseAPI.recalcRegimen>>>(
      '/api/admin/data-query/replay-recalc-regimen',
      data,
      { timeout: PK_CALCULATION_TIMEOUT },
    ).then(r => r.data),
  replayPkFormula: (data: Parameters<typeof doseAPI.pkFormula>[0]) =>
    adminAxios.post<PKFormulaResult>('/api/admin/data-query/replay-pk-formula', data, { timeout: PK_CALCULATION_TIMEOUT }).then(r => r.data),
  replayPkFormulaRecalcRegimen: (data: Parameters<typeof doseAPI.recalcPkFormulaRegimen>[0]) =>
    adminAxios.post<Awaited<ReturnType<typeof doseAPI.recalcPkFormulaRegimen>>>(
      '/api/admin/data-query/replay-pk-formula/recalc-regimen', data, { timeout: PK_CALCULATION_TIMEOUT },
    ).then(r => r.data),
  replayInterpret: (
    calculationId: string,
    selectedRegimen: InterpretationRegimenPayload,
    selectedMethod?: string,
  ) =>
    adminAxios.post<{ clinical_interpretation: string }>('/api/admin/data-query/replay-interpret', {
      calculation_id: calculationId,
      selected_regimen: selectedRegimen,
      selected_method: selectedMethod,
    }).then(r => r.data.clinical_interpretation),
  replayReportGenerate: (
    calculationId: string,
    customInterpretation?: string,
    selectedRegimen?: Record<string, any>,
    combinedCurve?: Record<string, any>,
    tdmPoints?: Array<Record<string, any>>,
    regimenHistory?: Array<Record<string, any>>,
    tdmData?: Array<Record<string, any>>,
  ) =>
    adminAxios.post<string>(
      `/api/admin/data-query/replay-report/${calculationId}`,
      {
        custom_interpretation: customInterpretation || null,
        selected_regimen: selectedRegimen || null,
        combined_curve: combinedCurve || null,
        tdm_points: tdmPoints || null,
        regimen_history: regimenHistory || null,
        tdm_data: tdmData || null,
      },
      { responseType: 'text' },
    ).then(r => r.data),
  deleteDataQueryRecords: (ids: string[]) =>
    adminAxios.delete('/api/admin/data-query/records', { data: { ids } }).then(r => r.data),
  exportExcel: (params: Record<string, any>) =>
    adminAxios.get('/api/admin/data-query/export', { params, responseType: 'blob' }).then(r => r),
  exportSelectedExcel: (ids: string[]) =>
    adminAxios.post('/api/admin/data-query/export-selected', { ids }, { responseType: 'blob' }).then(r => r),
  exportNonmem: (params: Record<string, any>) =>
    adminAxios.get('/api/admin/data-query/export-nonmem', { params, responseType: 'blob' }).then(r => r),
  exportSelectedNonmem: (ids: string[]) =>
    adminAxios.post('/api/admin/data-query/export-selected-nonmem', { ids }, { responseType: 'blob' }).then(r => r),

  // 套餐管理
  listPlans: () => adminAxios.get('/api/admin/plans').then(r => r.data),
  createPlan: (data: Record<string, any>) => adminAxios.post('/api/admin/plans', data).then(r => r.data),
  updatePlan: (planId: string, data: any) => adminAxios.put(`/api/admin/plans/${planId}`, data).then(r => r.data),
  deletePlan: (planId: string) => adminAxios.delete(`/api/admin/plans/${planId}`).then(r => r.data),

  // 订单管理
  listOrders: (params?: any) => adminAxios.get('/api/admin/orders', { params }).then(r => r.data),
  updateOrder: (id: string, data: any) => adminAxios.patch(`/api/admin/orders/${id}`, data).then(r => r.data),
  importOrders: (data: any) => adminAxios.post('/api/admin/orders/import', data).then(r => r.data),

  listPromoCodes: () => adminAxios.get('/api/admin/promo-codes').then(r => r.data),
  createPromoCode: (data: any) => adminAxios.post('/api/admin/promo-codes', data).then(r => r.data),
  deletePromoCode: (id: string) => adminAxios.delete(`/api/admin/promo-codes/${id}`).then(r => r.data),

  // AI用量
  aiUsage: (days = 30) => adminAxios.get('/api/admin/ai-usage', { params: { days } }).then(r => r.data),

  // 收支统计
  revenue: (months = 6) => adminAxios.get('/api/admin/revenue', { params: { months } }).then(r => r.data),

  // 激活码管理
  listActivationCodes: (params?: Record<string, any>) =>
    adminAxios.get('/api/admin/activation-codes', { params }).then(r => r.data),
  generateActivationCodes: (data: Record<string, any>) =>
    adminAxios.post('/api/admin/activation-codes/generate', data).then(r => r.data),
  revokeActivationCode: (id: string) =>
    adminAxios.patch(`/api/admin/activation-codes/${id}/revoke`).then(r => r.data),
  updateActivationCode: (id: string, data: Record<string, any>) =>
    adminAxios.patch(`/api/admin/activation-codes/${id}`, data).then(r => r.data),
  activationCodeStats: () =>
    adminAxios.get('/api/admin/activation-codes/stats').then(r => r.data),

  // 系统监控
  monitor: () => adminAxios.get('/api/admin/monitor').then(r => r.data),

  // 金币调整
  adjustCoin: (userId: string, amount: number, reason: string) =>
    adminAxios.post(`/api/admin/users/${userId}/coins/adjust`, { amount, reason }).then(r => r.data),
  listCoinTransactions: (params?: Record<string, any>) =>
    adminAxios.get('/api/admin/coin-transactions', { params }).then(r => r.data),
  exportCoinTransactions: (params?: Record<string, any>) =>
    adminAxios.get('/api/admin/coin-transactions/export', { params, responseType: 'blob' }).then(r => r),

  // 任务管理
  listTasks: (params?: Record<string, any>) =>
    adminAxios.get('/api/admin/tasks', { params }).then(r => r.data),
  createTask: (data: Record<string, any>) =>
    adminAxios.post('/api/admin/tasks', data).then(r => r.data),
  updateTask: (taskId: string, data: Record<string, any>) =>
    adminAxios.put(`/api/admin/tasks/${taskId}`, data).then(r => r.data),
  publishTask: (taskId: string) =>
    adminAxios.post(`/api/admin/tasks/${taskId}/publish`).then(r => r.data),
  cancelTask: (taskId: string) =>
    adminAxios.post(`/api/admin/tasks/${taskId}/cancel`).then(r => r.data),
  getApplications: (taskId: string) =>
    adminAxios.get(`/api/admin/tasks/${taskId}/applications`).then(r => r.data),
  approveApplication: (taskId: string, appId: string) =>
    adminAxios.post(`/api/admin/tasks/${taskId}/applications/${appId}/approve`).then(r => r.data),
  rejectApplication: (taskId: string, appId: string, note = '') =>
    adminAxios.post(`/api/admin/tasks/${taskId}/applications/${appId}/reject`, { note }).then(r => r.data),
  approveCompletion: (taskId: string, comment = '') =>
    adminAxios.post(`/api/admin/tasks/${taskId}/approve-completion`, { comment }).then(r => r.data),
  rejectCompletion: (taskId: string, comment = '') =>
    adminAxios.post(`/api/admin/tasks/${taskId}/reject-completion`, { comment }).then(r => r.data),
  taskStats: () =>
    adminAxios.get('/api/admin/tasks/stats').then(r => r.data),

  // 灵活边界条件
  getBoundaryGroups: (modelId: string) =>
    adminAxios.get(`/api/admin/models/${modelId}/boundary-groups`).then(r => r.data),
  createBoundaryGroup: (modelId: string, data: any) =>
    adminAxios.post(`/api/admin/models/${modelId}/boundary-groups`, data).then(r => r.data),
  updateBoundaryGroup: (groupId: number, data: any) =>
    adminAxios.put(`/api/admin/boundary-groups/${groupId}`, data).then(r => r.data),
  deleteBoundaryGroup: (groupId: number) =>
    adminAxios.delete(`/api/admin/boundary-groups/${groupId}`).then(r => r.data),
  createBoundaryRule: (groupId: number, data: any) =>
    adminAxios.post(`/api/admin/boundary-groups/${groupId}/rules`, data).then(r => r.data),
  updateBoundaryRule: (ruleId: number, data: any) =>
    adminAxios.put(`/api/admin/boundary-rules/${ruleId}`, data).then(r => r.data),
  deleteBoundaryRule: (ruleId: number) =>
    adminAxios.delete(`/api/admin/boundary-rules/${ruleId}`).then(r => r.data),
  createCategoricalBoundary: (groupId: number, data: any) =>
    adminAxios.post(`/api/admin/boundary-groups/${groupId}/categorical-rules`, data).then(r => r.data),
  updateCategoricalBoundary: (ruleId: number, data: any) =>
    adminAxios.put(`/api/admin/categorical-boundaries/${ruleId}`, data).then(r => r.data),
  deleteCategoricalBoundary: (ruleId: number) =>
    adminAxios.delete(`/api/admin/categorical-boundaries/${ruleId}`).then(r => r.data),
  getCovariateDefinitions: () =>
    adminAxios.get('/api/admin/covariates/definitions').then(r => r.data),

  getBoundaryTemplates: (populationTag?: string) =>
    adminAxios.get('/api/admin/boundary-templates', { params: { population_tag: populationTag } }).then(r => r.data),
  createBoundaryGroupFromTemplate: (modelId: string, templateId: number) =>
    adminAxios.post(`/api/admin/models/${modelId}/boundary-groups/from-template`, { template_id: templateId }).then(r => r.data),

  // 批量操作
  batchUpdateBoundaryGroups: (groupIds: number[], updates: any) =>
    adminAxios.post('/api/admin/boundary-groups/batch-update', { group_ids: groupIds, updates }).then(r => r.data),
};

// ========== 通知 ==========
export const notificationAPI = {
  getAnnouncements: () => api.get('/api/notifications/announcements').then(r => r.data),
};

// ========== 公开站点配置 ==========
export const siteAPI = {
  getConfig: (key: string) => api.get(`/api/site/config/${key}`).then(r => r.data),
  getOAuthProviders: () => api.get('/api/site/oauth-providers').then(r => r.data?.providers as string[] ?? []),
  getLLMProviders: () =>
    api.get('/api/site/llm-providers').then(r => r.data as {
      providers: Array<{
        id: string;
        display_name: string;
        protocol: string;
        configured: boolean;
        enabled: boolean;
        active: boolean;
        is_default: boolean;
        resolved_model: string;
        resolved_base_url: string;
        supports_function_calling: boolean;
      }>;
      default_provider: string;
    }),
  getLandingContent: () => api.get('/api/site/landing-content').then(r => r.data),
  getAgentIntakeTemplates: () => api.get<{ templates: Array<{ title: string; content: string }> }>('/api/site/agent-intake-templates').then(r => r.data.templates),
};

// ========== 订阅管理 ==========
export const subscriptionAPI = {
  getPlans: () => api.get('/api/subscriptions/plans').then(r => r.data),
  getCurrent: () => api.get('/api/subscriptions/current').then(r => r.data),
  getFeatures: () => api.get<{ features: Record<string, boolean>; limits: Record<string, number> }>('/api/subscriptions/features').then(r => r.data),
  getAll: () => api.get('/api/subscriptions/all').then(r => r.data),
};

export const activationAPI = {
  activate: (code: string) => api.post('/api/subscriptions/activate', { code }).then(r => r.data),
  history: () => api.get('/api/subscriptions/activation-history').then(r => r.data),
};

// ========== 金币系统 ==========
export const coinAPI = {
  getBalance: () => api.get('/api/users/me/coins').then(r => r.data),
  getHistory: (page = 1, size = 20) =>
    api.get('/api/users/me/coins/history', { params: { page, size } }).then(r => r.data),
  getReferralFunnel: (params?: { start_date?: string; end_date?: string }) =>
    api.get('/api/users/me/referrals', { params }).then(r => r.data),
  exchange: (plan_name: string, duration_days: number) =>
    api.post('/api/users/me/coins/exchange', { plan_name, duration_days }).then(r => r.data),
  getNotificationSettings: () => api.get('/api/users/me/notification-settings').then(r => r.data),
  updateNotificationSettings: (data: { email_notify: boolean; webhook_url?: string | null }) =>
    api.put('/api/users/me/notification-settings', data).then(r => r.data),
};

// ========== 文件上传 ==========
export const uploadAPI = {
  upload: (file: File, category: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    // 让浏览器自动设置 multipart/form-data
    return api.post('/api/uploads', fd).then(r => r.data);
  },
};

export const adminUploadAPI = {
  upload: (file: File, category: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    return adminAxios.post('/api/uploads', fd).then(r => r.data);
  },
};

export const taskAPI = {
  list: (params?: Record<string, any>) =>
    api.get('/api/tasks/', { params }).then(r => r.data),
  detail: (taskId: string) =>
    api.get(`/api/tasks/${taskId}`).then(r => r.data),
  apply: (taskId: string, data: { experience: string; credentials?: string }) =>
    api.post(`/api/tasks/${taskId}/apply`, data).then(r => r.data),
  withdrawApplication: (taskId: string) =>
    api.delete(`/api/tasks/${taskId}/apply`).then(r => r.data),
  start: (taskId: string) =>
    api.post(`/api/tasks/${taskId}/start`).then(r => r.data),
  submit: (taskId: string, data: { submission_text?: string; submission_report?: Record<string, any> }) =>
    api.post(`/api/tasks/${taskId}/submit`, data).then(r => r.data),
  myTasks: () =>
    api.get('/api/tasks/my').then(r => r.data),
};

export const covariateAPI = {
  definitions: () =>
    api.get('/api/covariates/definitions').then(r => r.data),
  derive: (data: {
    covariates: Record<string, any>;
    target_keys?: string[];
    population_group?: string | null;
    selected_population_group?: string | null;
  }) =>
    api.post('/api/covariates/derive', data).then(r => r.data),
  /** 获取药物级协变量 */
  byDrug: (drugId: string, populationGroup?: string) =>
    api.get(`/api/covariates/by-drug/${drugId}`, {
      params: populationGroup ? { population_group: populationGroup } : undefined,
    }).then(r => r.data),
  resolveScope: (data: {
    drug_id: string;
    selected_population_group?: string | null;
    covariates?: Record<string, any>;
    scope_locked?: boolean;
  }) =>
    api.post<ScopeResolutionResult>('/api/covariates/resolve-scope', data).then(r => r.data),
  scopeMatrix: (drugId: string) =>
    adminAxios.get<DrugCovariateScopeMatrixResponse>(`/api/covariates/drug-scope-matrix/${drugId}`).then(r => r.data),
  updateScopeMatrix: (drugId: string, rows: any[]) =>
    adminAxios.put<DrugCovariateScopeMatrixResponse>(`/api/covariates/drug-scope-matrix/${drugId}`, { rows }).then(r => r.data),
  addScopeMatrixItems: (drugId: string, covariateKeys: string[]) =>
    adminAxios.post<DrugCovariateScopeMatrixResponse>(`/api/covariates/drug-scope-matrix/${drugId}/items`, {
      covariate_keys: covariateKeys,
    }).then(r => r.data),
  removeScopeMatrixItem: (drugId: string, covariateKey: string) =>
    adminAxios.delete<DrugCovariateScopeMatrixResponse>(`/api/covariates/drug-scope-matrix/${drugId}/items/${covariateKey}`).then(r => r.data),
  /** 获取模型级协变量绑定 */
  byModel: (modelId: string) =>
    adminAxios.get(`/api/covariates/by-model/${modelId}`).then(r => r.data),
  createDefinition: (data: any) =>
    adminAxios.post('/api/covariates/definitions', data).then(r => r.data),
  updateDefinition: (key: string, data: any) =>
    adminAxios.put(`/api/covariates/definitions/${key}`, data).then(r => r.data),
  deleteDefinition: (key: string) =>
    adminAxios.delete(`/api/covariates/definitions/${key}`).then(r => r.data),
  updateModelBindings: (modelId: string, bindings: any[]) =>
    adminAxios.put(`/api/covariates/model-bindings/${modelId}`, { bindings }).then(r => r.data),
  updateDrugBindings: (drugId: string, bindings: any[], populationGroup?: string) =>
    adminAxios.put(`/api/covariates/drug-bindings/${drugId}`, { bindings }, {
      params: populationGroup ? { population_group: populationGroup } : undefined,
    }).then(r => r.data),
  syncDrugBindings: (drugId: string, populationGroup?: string) =>
    adminAxios.post(`/api/covariates/drug-bindings/${drugId}/sync`, undefined, {
      params: populationGroup ? { population_group: populationGroup } : undefined,
    }).then(r => r.data),
  modelsByCovariate: (key: string) =>
    adminAxios.get(`/api/covariates/definitions/${key}/models`).then(r => r.data),
  /** 获取单个协变量的单位变体列表 */
  getDerivationRules: (key: string) =>
    adminAxios.get(`/api/covariates/definitions/${key}/derivation-rules`).then(r => r.data),
  updateDerivationRules: (key: string, rules: any[]) =>
    adminAxios.put(`/api/covariates/definitions/${key}/derivation-rules`, { rules }).then(r => r.data),
  testDerivationRules: (key: string, data: { covariates: Record<string, any>; rules: any[] }) =>
    adminAxios.post(`/api/covariates/definitions/${key}/derivation-rules/test`, data).then(r => r.data),
  getUnitVariants: (key: string) =>
    adminAxios.get(`/api/covariates/definitions/${key}/unit-variants`).then(r => r.data),
  /** 批量更新单位变体 */
  updateUnitVariants: (key: string, variants: any[]) =>
    adminAxios.put(`/api/covariates/definitions/${key}/unit-variants`, { variants }).then(r => r.data),
};

/** CMS API */
export const cmsAPI = {
  getSections: () => api.get('/api/cms/sections').then(r => r.data),
  getPage: (slug: string) => api.get(`/api/cms/pages/${slug}`).then(r => r.data),
  adminGetSections: () => adminAxios.get('/api/admin/cms/sections').then(r => r.data),
  /** 管理员：创建板块 */
  adminCreateSection: (data: any) => adminAxios.post('/api/admin/cms/sections', data).then(r => r.data),
  /** 管理员：更新板块 */
  adminUpdateSection: (id: string, data: any) => adminAxios.put(`/api/admin/cms/sections/${id}`, data).then(r => r.data),
  /** 管理员：删除板块 */
  adminDeleteSection: (id: string) => adminAxios.delete(`/api/admin/cms/sections/${id}`).then(r => r.data),
  adminGetPages: (sectionId?: string) => adminAxios.get('/api/admin/cms/pages', { params: { section_id: sectionId } }).then(r => r.data),
  /** 管理员：创建页面 */
  adminCreatePage: (data: any) => adminAxios.post('/api/admin/cms/pages', data).then(r => r.data),
  /** 管理员：更新页面 */
  adminUpdatePage: (id: string, data: any) => adminAxios.put(`/api/admin/cms/pages/${id}`, data).then(r => r.data),
  /** 管理员：删除页面 */
  adminDeletePage: (id: string) => adminAxios.delete(`/api/admin/cms/pages/${id}`).then(r => r.data),
};

/** 启动时加载协变量定义到前端注册表 */
export async function loadCovariateDefinitions() {
  try {
    const { setDynamicRegistry } = await import('../config/covariateRegistry');
    const data = await covariateAPI.definitions();
    if (data?.definitions?.length) {
      setDynamicRegistry(data.definitions);
    }
  } catch {
  }
}

export default api;
